import { Box, Button, Stack, type TextFieldProps } from '@mui/material';
import { canadaProvinces, usStates } from '@provision/location-utils';
import { useFormContext } from 'react-hook-form';
import { FormAutocomplete, FormPhoneInput, FormTextField } from 'src/components/Forms';
import type { AutoCompleteOption } from 'src/components/Forms/types';
import { EMAIL_VALIDATION_REGEX } from 'src/components/Forms/validations';
import { timeZones } from 'src/constants/timezone';
import { InfoCard, PanelSectionTitle, SectionDivider } from './styled';
import { CompanyInfoField, CompanyInfoFormValues } from './types';

interface CompanyInfoFormProps {
  isAdmin: boolean;
  isUpdating: boolean;
  onSave: () => void;
}

const STATE_OPTIONS: AutoCompleteOption[] = [...usStates, ...canadaProvinces].map((state) => ({
  id: state.value,
  value: state.value,
  label: `${state.text} (${state.value})`,
}));

const TIMEZONE_OPTIONS = [...timeZones];

const TEXT_FIELD_PROPS: Partial<TextFieldProps> = {
  variant: 'outlined',
  size: 'small',
};

export const CompanyInfoForm = ({ isAdmin, isUpdating, onSave }: CompanyInfoFormProps) => {
  const {
    formState: { isValid, isDirty },
  } = useFormContext<CompanyInfoFormValues>();

  const disabled = !isAdmin;
  const isSaveDisabled = !isValid || !isDirty || isUpdating;

  return (
    <InfoCard>
      <PanelSectionTitle>Company Details</PanelSectionTitle>

      <Stack spacing={1.5} sx={{ flex: 1 }}>
        <FormTextField
          type="textfield"
          name={CompanyInfoField.Address}
          label="Office Address"
          required="Office Address is required"
          disabled={disabled}
          textFieldProps={TEXT_FIELD_PROPS}
        />

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <FormTextField
              type="textfield"
              name={CompanyInfoField.City}
              label="City"
              required="City is required"
              disabled={disabled}
              textFieldProps={{ ...TEXT_FIELD_PROPS, fullWidth: true }}
            />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <FormAutocomplete
              type="autocomplete"
              name={CompanyInfoField.State}
              label="State/Province"
              required="State/Province is required"
              disabled={disabled}
              options={STATE_OPTIONS}
              textFieldProps={{ ...TEXT_FIELD_PROPS, fullWidth: true }}
            />
          </Box>
        </Stack>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <FormTextField
              type="textfield"
              name={CompanyInfoField.Zip}
              label="Zip/Postal Code"
              required="Zip/Postal Code is required"
              disabled={disabled}
              textFieldProps={{ ...TEXT_FIELD_PROPS, fullWidth: true }}
            />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <FormPhoneInput
              type="phone"
              name={CompanyInfoField.OfficePhone}
              label="Office Phone"
              disabled={disabled}
              variant="outlined"
              sx={{ width: '100%' }}
              textFieldProps={{ ...TEXT_FIELD_PROPS, fullWidth: true, disabled }}
            />
          </Box>
        </Stack>

        <FormAutocomplete
          type="autocomplete"
          name={CompanyInfoField.TimeZone}
          label="Timezone"
          required="Timezone is required"
          disabled={disabled}
          options={TIMEZONE_OPTIONS}
          textFieldProps={TEXT_FIELD_PROPS}
        />

        <FormTextField
          type="textfield"
          name={CompanyInfoField.WebsiteUrl}
          label="Company Website"
          required="Company Website is required"
          disabled={disabled}
          textFieldProps={TEXT_FIELD_PROPS}
        />

        <SectionDivider />

        <Stack spacing={1.5}>
          <PanelSectionTitle sx={{ mb: 0 }}>Billing Contacts</PanelSectionTitle>
          <FormTextField
            type="textfield"
            name={CompanyInfoField.BillingEmail}
            label="Billing Email"
            required="Billing Email is required"
            pattern={EMAIL_VALIDATION_REGEX}
            disabled={disabled}
            textFieldProps={TEXT_FIELD_PROPS}
          />
        </Stack>
      </Stack>

      {isAdmin ? (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="flex-end"
          sx={{ mt: 4, pt: 3, borderTop: 1, borderColor: 'divider' }}
        >
          <Button
            variant="contained"
            disabled={isSaveDisabled}
            onClick={onSave}
            sx={{
              minWidth: 120,
              width: {
                xs: '100%',
                sm: 'auto',
              },
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: 'none',
              '&:hover': {
                boxShadow: 'none',
              },
            }}
          >
            {isUpdating ? 'Saving...' : 'Save Changes'}
          </Button>
        </Stack>
      ) : null}
    </InfoCard>
  );
};
